/**
 * Pure Logik für den Hilfe-Bereich: YouTube-ID-Parsing,
 * Auswahl der Signup-Tutorials, Kategorien-Gruppierung.
 */
import { TutorialVideo } from '@/types';

const YT_URL_RE =
  /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/;
const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/** Extrahiert die 11-stellige Video-ID aus allen üblichen YouTube-URL-Formen oder einer rohen ID. */
export function parseYoutubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (YT_ID_RE.test(trimmed)) return trimmed;
  const match = trimmed.match(YT_URL_RE);
  return match ? match[1] : null;
}

/** Ungelesene, veröffentlichte Signup-Videos, nach sortOrder. */
export function selectSignupTutorials(
  videos: TutorialVideo[],
  readIds: Set<string>,
): TutorialVideo[] {
  return videos
    .filter(v => v.showOnSignup && v.published && !readIds.has(v.id))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export interface TutorialGroup {
  category: string;
  videos: TutorialVideo[];
}

const FALLBACK_CATEGORY = 'Allgemein';

/** Gruppiert nach Kategorie (null → „Allgemein"), Gruppen nach kleinstem sortOrder, Videos nach sortOrder. */
export function groupTutorials(videos: TutorialVideo[]): TutorialGroup[] {
  const map = new Map<string, TutorialVideo[]>();
  for (const v of videos) {
    const cat = v.category?.trim() || FALLBACK_CATEGORY;
    const list = map.get(cat) ?? [];
    list.push(v);
    map.set(cat, list);
  }
  return [...map.entries()]
    .map(([category, vids]) => ({
      category,
      videos: [...vids].sort((a, b) => a.sortOrder - b.sortOrder),
    }))
    .sort(
      (a, b) =>
        Math.min(...a.videos.map(v => v.sortOrder)) -
        Math.min(...b.videos.map(v => v.sortOrder)),
    );
}
